/**
 * EasyEdaPluginInstallCard — companion to `KicadPluginInstallCard`,
 * but for the EasyEDA browser extension. Same `compact` shape so it
 * slots into `PluginSyncCard`, `EdaConnectDialog`, and `ApiKeysPage`
 * the same way.
 *
 * Two surfaces depending on release feed state:
 *
 *   * **Release exists** → version chip + size + Download button (302
 *     to the GitHub-hosted zip via the backend's `/download` endpoint),
 *     mirroring the KiCad card. Install accordion walks through
 *     extracting the zip and `chrome://extensions → Load unpacked`.
 *
 *   * **No release yet (404)** → "Build from source" CTA pointing at
 *     `plugins/easyeda-circuit-forge` on GitHub. Honest about the Beta
 *     state without nagging users with a scary error banner.
 *
 * Distribution model differs from KiCad in one key way: a downloaded
 * `.zip` still cannot be double-clicked to install — Chrome MV3 only
 * accepts unpacked dirs via Developer mode. The Web Store listing is
 * the only path to a one-click install, and it's still on the roadmap.
 * The accordion reflects this reality.
 */
import {
  CheckCircleRounded,
  DownloadRounded,
  ExpandMoreRounded,
  ExtensionRounded,
  HelpOutlineRounded,
  LaunchRounded,
  StorefrontRounded,
  WarningAmberRounded,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";

import { pluginDistributionApi } from "../services/api";

const REPO_PLUGIN_URL =
  "https://github.com/octoco-ltd/circuit-forge/tree/main/plugins/easyeda-circuit-forge";

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

interface EasyEdaPluginInstallCardProps {
  /** When true, renders a slimmer variant for embedding in other cards
   *  (e.g. the project Plugin Sync card). The default `false` is the
   *  standalone Settings → API Keys page shape. Mirrors the KiCad
   *  card's prop shape so callers can drop the two side-by-side. */
  compact?: boolean;
}

export function EasyEdaPluginInstallCard({
  compact = false,
}: EasyEdaPluginInstallCardProps) {
  const release = useQuery({
    queryKey: ["easyeda-plugin-latest"],
    queryFn: pluginDistributionApi.getLatestEasyEdaRelease,
    // Same 5-min cache as the KiCad card — aligns with the backend's
    // own GitHub-API cache so we don't burn fetches on tab switches.
    staleTime: 5 * 60 * 1000,
    // 404 (no release published yet) and 5xx are both expected states
    // for this card today — let them surface as `release.error` rather
    // than retrying and pretending the server is flaky.
    retry: 1,
  });

  const downloadUrl = pluginDistributionApi.downloadEasyEdaUrl();
  const hasRelease = Boolean(release.data?.asset);

  // Header row toggles between two states based on whether a release
  // exists yet. The KiCad card has the same dual-mode shape, just
  // condensed because KiCad has always had a release.
  const HeaderRow: ReactNode = release.isLoading ? (
    <Stack direction="row" spacing={2} alignItems="center">
      <CircularProgress size={20} />
      <Skeleton variant="text" width={180} height={28} />
    </Stack>
  ) : hasRelease && release.data ? (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={2}
      alignItems={{ sm: "center" }}
      justifyContent="space-between"
    >
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
        <Typography variant="body2">
          <strong>v{release.data.version}</strong>
        </Typography>
        {release.data.asset && (
          <Chip
            size="small"
            variant="outlined"
            label={formatBytes(release.data.asset.size)}
          />
        )}
        {release.data.published_at && (
          <Typography variant="caption" color="text.secondary">
            Released {new Date(release.data.published_at).toLocaleDateString()}
          </Typography>
        )}
      </Stack>

      <Button
        component="a"
        href={downloadUrl}
        variant="contained"
        startIcon={<DownloadRounded />}
      >
        Download extension
      </Button>
    </Stack>
  ) : (
    // Pre-release / fallback. The Beta chip + "build from source"
    // caption is the honest signal that no zip has been cut yet.
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={2}
      alignItems={{ sm: "center" }}
      justifyContent="space-between"
    >
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
        <Chip size="small" label="Beta" color="primary" variant="outlined" />
        <Typography variant="caption" color="text.secondary">
          Chrome / Edge / Brave · build from source
        </Typography>
      </Stack>

      <Button
        component="a"
        href={REPO_PLUGIN_URL}
        target="_blank"
        rel="noreferrer"
        variant="contained"
        startIcon={<LaunchRounded />}
      >
        Get the extension
      </Button>
    </Stack>
  );

  // Install steps differ slightly between "downloaded zip" and "built
  // from source" — only the first step changes. Keep the rest shared.
  const FirstStep: ReactNode = hasRelease ? (
    <ListItem disableGutters>
      <ListItemIcon sx={{ minWidth: 32 }}>
        <CheckCircleRounded fontSize="small" color="primary" />
      </ListItemIcon>
      <ListItemText
        primary={
          <>
            Click <strong>Download extension</strong> above, then unzip it
            somewhere stable (Chrome reads the folder every browser launch).
          </>
        }
        primaryTypographyProps={{ variant: "body2" }}
      />
    </ListItem>
  ) : (
    <ListItem disableGutters>
      <ListItemIcon sx={{ minWidth: 32 }}>
        <CheckCircleRounded fontSize="small" color="primary" />
      </ListItemIcon>
      <ListItemText
        primary={
          <>
            Clone the repo, then in <code>plugins/easyeda-circuit-forge</code>{" "}
            run <code>npm install &amp;&amp; npm run build</code>.
          </>
        }
        primaryTypographyProps={{ variant: "body2" }}
      />
    </ListItem>
  );

  const Body = (
    <Stack spacing={2}>
      {HeaderRow}

      {/* A 502/503 means GitHub or our distribution config is down —
          surface so the user knows it's not "their" problem. A 404
          (no release yet) is the expected pre-release state and is
          handled by the HeaderRow falling back to the build CTA. */}
      {release.isError &&
        (release.error as { response?: { status?: number } })?.response
          ?.status !== 404 && (
          <Alert severity="warning" icon={<WarningAmberRounded />}>
            Couldn't reach the extension release feed. You can still grab the
            source from{" "}
            <a href={REPO_PLUGIN_URL} target="_blank" rel="noreferrer">
              the plugins directory on GitHub
            </a>
            .
          </Alert>
        )}

      <Accordion
        elevation={0}
        disableGutters
        sx={{ "&:before": { display: "none" } }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreRounded />}
          sx={{
            px: 0,
            minHeight: 36,
            "& .MuiAccordionSummary-content": { my: 0.5 },
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <HelpOutlineRounded fontSize="small" color="action" />
            <Typography variant="body2">How to install</Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0, pt: 0 }}>
          <List dense>
            {FirstStep}
            <ListItem disableGutters>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CheckCircleRounded fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText
                primary={
                  <>
                    Open <code>chrome://extensions</code> (or{" "}
                    <code>edge://extensions</code>), turn on{" "}
                    <strong>Developer mode</strong>, click{" "}
                    <strong>Load unpacked</strong>, and pick the{" "}
                    {hasRelease ? (
                      <>extracted folder</>
                    ) : (
                      <>
                        <code>dist/</code> folder
                      </>
                    )}
                    .
                  </>
                }
                primaryTypographyProps={{ variant: "body2" }}
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CheckCircleRounded fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText
                primary={
                  <>
                    Pin the puzzle-piece icon, open the popup, paste your API
                    key, pick this project, and click <em>Save selection</em>.
                  </>
                }
                primaryTypographyProps={{ variant: "body2" }}
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CheckCircleRounded fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText
                primary={
                  <>
                    In EasyEDA, export your design via{" "}
                    <strong>Document → Export → Altium Netlist</strong>, then
                    click <em>Push to Circuit Forge</em> in the popup.
                  </>
                }
                primaryTypographyProps={{ variant: "body2" }}
              />
            </ListItem>
          </List>
        </AccordionDetails>
      </Accordion>

      <Box
        sx={(t) => ({
          p: 1.5,
          borderRadius: 1,
          border: `1px dashed ${t.palette.divider}`,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        })}
      >
        <StorefrontRounded fontSize="small" color="action" />
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          Chrome Web Store listing is on the roadmap — until then, Developer
          mode + Load unpacked is the install path.
        </Typography>
      </Box>
    </Stack>
  );

  // Same branded-header pattern as KicadPluginInstallCard so the two
  // sit side-by-side in the project Sync page as obviously parallel
  // install paths. The teal avatar + "Browser" chip are the form-
  // factor signal that distinguishes this from the desktop plugin.
  const Header = (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Avatar
        variant="rounded"
        sx={{
          width: 36,
          height: 36,
          bgcolor: "#1B9E77",
          color: "common.white",
          fontWeight: 700,
        }}
      >
        E
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            EasyEDA extension
          </Typography>
          <Chip
            size="small"
            icon={<ExtensionRounded sx={{ fontSize: 14 }} />}
            label="Browser"
            variant="outlined"
            sx={{ height: 20 }}
          />
        </Stack>
        <Typography variant="caption" color="text.secondary">
          Chrome, Edge, Brave
        </Typography>
      </Box>
    </Stack>
  );

  if (compact) {
    return (
      <Paper variant="outlined" sx={{ p: 2.5, height: "100%" }}>
        <Stack spacing={2}>
          {Header}
          <Divider />
          {Body}
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack spacing={2}>
        {Header}
        <Typography variant="body2" color="text.secondary">
          A browser extension for Chrome, Edge, and Brave. Push your EasyEDA
          designs to Circuit Forge in one click.
        </Typography>
        <Divider />
        {Body}
      </Stack>
    </Paper>
  );
}
